import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import UserContentNavigation from "pages/Account/UserContentNavigation";
import Page from "pages/Page";
import GlobalContext from "contexts/GlobalContext";
import webPlatformAdapter from "common/Adapters/WebPlatformAdapter/WebPlatformAdapter";
import pgAdapter from "common/Adapters/PGAdapter/PGAdapter";
import {
  LogoLinkedin32,
  LogoXing32,
  Form,
  FormFields,
  InfoBlock,
  dateFormatter,
  Content,
  MultiselectDropdown,
  usePathFactory,
  useTranslator,
} from "@sia/style-guide";
import { WebsiteInput } from "components/WebsiteInput";
import "./Formation.scss";
import erpAdapter from "common/Adapters/ERPAdapter";
import { BackButton } from "@sia/style-guide/dist/components/BackButton/BackButton";

const FormationPage = () => {
  const translate = useTranslator();
  const history = useHistory();
  const pathFactory = usePathFactory();
  const GlobalContextValue = useContext(GlobalContext);
  const user = GlobalContextValue.user;
  const language = GlobalContextValue.language;
  const [wasFormationFetchInitialized, setWasFormationFetchInitialized] = useState(false);
  const [wasProfessionalNetworksFetchInitialized, setWasProfessionalNetworksFetchInitialized] = useState(false);
  const [formations, setFormations] = useState([]);
  const [linkedin, setLinkedin] = useState("");
  const [linkedInInvalidText, setLinkedInInvalidText] = useState("");
  const [xing, setXing] = useState("");
  const [xingInvalidText, setXingInvalidText] = useState("");

  const [specialisations, setSpecialisations] = useState([]);
  const [wasSpecialisationsFetchInitialized, setWasSpecialisationsFetchInitialized] = useState(false);
  const [isSpecialisationOpen, setIsSpecialisationOpen] = useState(false);

  const [branches, setBranches] = useState([]);
  const [wasBranchesFetchInitialized, setWasBranchesFetchInitialized] = useState(false);
  const [isBranchOpen, setIsBranchOpen] = useState(false);

  const [wasSinglesWorkInfoFetched, setWasSinglesWorkInfoFetched] = useState(false);

  const title = translate("account.customer-data.formation");
  const translationKeys = {
    type: "account.customer-data.formation.formation.type",
    diplomaTitle: "account.customer-data.formation.formation.diploma-title",
  };

  useEffect(() => {
    if (user && !wasFormationFetchInitialized) {
      setWasFormationFetchInitialized(true);
      webPlatformAdapter.getFormationSingle(language, (response) => {
        const formations = response.data.map((rawFormation) => {
          return {
            type: rawFormation.type.name,
            diplomaTitle: rawFormation.diploma_title,
            from: rawFormation.from_date,
            to: rawFormation.to_date,
          };
        });
        setFormations(formations);
      });
    }

    if (user && !wasProfessionalNetworksFetchInitialized) {
      setWasProfessionalNetworksFetchInitialized(true);
      webPlatformAdapter.getProfessionalNetworksSingle(language, (response) => {
        const linkedin = response.data[0].linkedin;
        const xing = response.data[0].xing;
        if (linkedin) {
          setLinkedin(linkedin);
        }
        if (xing) {
          setXing(xing);
        }
      });
    }
    if (user && !wasSinglesWorkInfoFetched && !wasSpecialisationsFetchInitialized && !wasBranchesFetchInitialized) {
      pgAdapter.getSinglesWorkInfo(language, ({ data }) => {
        const { work_specialisations, work_branches } = data;
        const work_specialisation_ids = work_specialisations?.map((e) => e.id) || [];
        const work_branch_ids = work_branches?.map((e) => e.id) || [];
        if (!wasSpecialisationsFetchInitialized && specialisations.length === 0) {
          webPlatformAdapter.getAreasOfActivity(language, ({ data }) => {
            let formatedData = data.map((e) => ({ id: e.id, label: e.name, active: work_specialisation_ids.includes(e.id) }));
            setSpecialisations(formatedData);
          });
          setWasSpecialisationsFetchInitialized(true);
        } else {
          let alreadyFetchSpecialisations = specialisations.map((e) => ({ ...e, active: work_specialisation_ids.includes(e.id) }));
          setSpecialisations(alreadyFetchSpecialisations);
        }

        if (!wasBranchesFetchInitialized && branches.length === 0) {
          webPlatformAdapter.getIndustrySectors(language, ({ data }) => {
            let formatedData = data.map((e) => ({ id: e.id, label: e.name, active: work_branch_ids.includes(e.id) }));
            setBranches(formatedData);
          });
          setWasBranchesFetchInitialized(true);
        } else {
          let alreadyFetchBranches = branches.map((e) => ({ ...e, active: work_branch_ids.includes(e.id) }));
          setBranches(alreadyFetchBranches);
        }
      });
      setWasSinglesWorkInfoFetched(true);
    }
  }, [
    user,
    wasFormationFetchInitialized,
    wasProfessionalNetworksFetchInitialized,
    language,
    wasBranchesFetchInitialized,
    wasSpecialisationsFetchInitialized,
    wasSinglesWorkInfoFetched,
    branches,
    specialisations,
  ]);
  const closeDropDowns = () => {
    setIsSpecialisationOpen(false);
    setIsBranchOpen(false);
  };
  // todo, optimize this workaround. see https://sia-webplattform.atlassian.net/browse/SW-243?focusedCommentId=10515
  const onSave = () => {
    setLinkedInInvalidText("");
    setXingInvalidText("");

    erpAdapter.fetch(
      "PATCH",
      "account/single/professional_info",
      {
        work_specialisation: specialisations.filter((e) => e.active).map((e) => e.id),
        work_branch: branches.filter((e) => e.active).map((e) => e.id),
        linkedin_profile: linkedin.trim().replace(/^https?:\/\//, ""),
        xing_profile: xing.trim().replace(/^https?:\/\//, ""),
      },
      () => {
        setWasSinglesWorkInfoFetched(false);
        setWasSpecialisationsFetchInitialized(false);
        setWasBranchesFetchInitialized(false);
        setWasProfessionalNetworksFetchInitialized(false);
      },
      null
    );
  };

  const renderFormations = () => {
    if (!formations.length) {
      return <p>{translate("data.no-items-found")}</p>;
    }
    return formations.map((formation, index) => (
      <InfoBlock key={index}>
        <span className={"label"}>{translate(translationKeys.type)}</span>
        <p>{formation.type}</p>

        <span className={"label"}>{translate(translationKeys.diplomaTitle)}</span>
        <p>{formation.diplomaTitle}</p>

        <span className={"label"}>{translate("date.start")}</span>
        <p>{dateFormatter.format(formation.from)}</p>

        <span className={"label"}>{translate("date.end")}</span>
        <p>{dateFormatter.format(formation.to)}</p>
      </InfoBlock>
    ));
  };

  const renderContent = () => {
    return (
      <>
        <h3>{translate("account.customer-data.formation.formation")}</h3>
        {renderFormations()}

        <h3 className="sia-padding-02-top">{translate("account.customer-data.formation.specialisations-and-branches")}</h3>
        <MultiselectDropdown
          id={"specialisations"}
          label={translate("account.customer-data.formation.specialisations")}
          isOpen={isSpecialisationOpen}
          toggle={() => {
            closeDropDowns();
            setIsSpecialisationOpen(!isSpecialisationOpen);
          }}
          items={specialisations}
          onClickItem={(event, item, updatedItems) => setSpecialisations(updatedItems)}
        />
        <MultiselectDropdown
          id={"branches"}
          label={translate("account.customer-data.formation.branches")}
          isOpen={isBranchOpen}
          toggle={() => {
            closeDropDowns();
            setIsBranchOpen(!isBranchOpen);
          }}
          items={branches}
          onClickItem={(event, item, updatedItems) => setBranches(updatedItems)}
        />
        <h3 className="sia-padding-02-top">{translate("account.customer-data.formation.professional-networks")}</h3>
        <Form primaryButtonLabel={translate("form.save")} onClickPrimaryButton={onSave}>
          <FormFields>
            <div className={"social-input"}>
              <LogoLinkedin32 />
              <WebsiteInput
                id={"linkedin"}
                label={translate("account.customer-data.formation.professional-networks.linkedin")}
                value={linkedin}
                onChange={(event, value, error) => {
                  setLinkedInInvalidText("");
                  setLinkedin(value);
                  if (error) {
                    setLinkedInInvalidText(error);
                  }
                }}
                invalidText={linkedInInvalidText}
              />
            </div>
            <div className={"social-input"}>
              <LogoXing32 />
              <WebsiteInput
                id={"xing"}
                label={translate("account.customer-data.formation.professional-networks.xing")}
                value={xing}
                onChange={(event, value, error) => {
                  setXingInvalidText("");
                  setXing(value);
                  if (error) {
                    setXingInvalidText(error);
                  }
                }}
                invalidText={xingInvalidText}
              />
            </div>
          </FormFields>
        </Form>
      </>
    );
  };

  return (
    <Page contentNavigation={<UserContentNavigation />} title={"account.name"}>
      <Content width={"slim"} centeredTitle={true}>
        <h1>
          <BackButton onClick={() => history.push(pathFactory.create("account/customer-data"))} />
          {title}
        </h1>
        {renderContent()}
      </Content>
    </Page>
  );
};
export default FormationPage;
